import { useMemo } from 'react'
import { Stack, Typography, FormControl } from '@vapor/material'
import { ResourceTypeTimeUnitOfMeasurement } from 'src/api/api.generated'
import ResettableTextField from 'src/app/pages/booking/reservation-insert/components/resettable-text-field'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { useTranslation } from 'react-i18next'
import { getTimePartOnly } from 'src/utils/date'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { FormItem } from 'hic-components'

type TotalReservationTimeProps = {
  days: number
  rackRate: number
  minRate: number
  quantity: number
  reservationTime: {
    from: Date
    to: Date
  }
  activity?: {
    from: Date
    to: Date
  }
  unitOfMeasurement: ResourceTypeTimeUnitOfMeasurement
  value?: number
  onChange: (value: number | null) => void
}

const TIME_BEGIN_OF_DAY = new Date(1970, 0, 1, 0, 0)
const TIME_END_OF_DAY = new Date(1970, 0, 1, 23, 59)

export const TotalReservationTime = (props: TotalReservationTimeProps) => {
  const { t } = useTranslation()
  const { currency } = useTenant()
  const { formatCurrency } = useFormatCurrency()
  const formatDate = useFormatDate()
  const resourceTypeActivity = useMemo(() => props.activity ?? { from: TIME_BEGIN_OF_DAY, to: TIME_END_OF_DAY }, [props.activity])

  const calculatedDailyPrice = useMemo(() => {
    const reservationInMinutes = Math.round((props.reservationTime.to.getTime() - props.reservationTime.from.getTime()) / (1000 * 60))
    let price = 0
    switch (props.unitOfMeasurement) {
      case 'QUARTER': {
        price = Math.ceil(reservationInMinutes / 15) * props.rackRate
        break
      }
      case 'HALF_HOUR': {
        price = Math.ceil(reservationInMinutes / 30) * props.rackRate
        break
      }
      case 'HOUR': {
        price = Math.ceil(reservationInMinutes / 60) * props.rackRate
        break
      }
      case 'HALF_DAY': {
        const activityInMinutes = Math.round((resourceTypeActivity.to.getTime() - resourceTypeActivity.from.getTime()) / (1000 * 60))
        const activityHalfDay = activityInMinutes / 2
        const timeFreeBeforeReservationStart = Math.round((getTimePartOnly(props.reservationTime.from).getTime() - resourceTypeActivity.from.getTime()) / (1000 * 60))
        const timeFreeAfterReservationStart = Math.round((resourceTypeActivity.to.getTime() - getTimePartOnly(props.reservationTime.to).getTime()) / (1000 * 60))
        //If cross-turn reservation return double price
        if (timeFreeBeforeReservationStart < activityHalfDay && timeFreeAfterReservationStart < activityHalfDay) price = 2 * props.rackRate
        price = Math.ceil(reservationInMinutes / activityHalfDay) * props.rackRate
        break
      }
      case 'DAY':
      default: {
        price = props.rackRate
        break
      }
    }
    props.onChange(price)
    return price
  }, [props.activity, props.rackRate, JSON.stringify(props.reservationTime), props.unitOfMeasurement, resourceTypeActivity])

  const udmLabel = useMemo(() => {
    switch (props.unitOfMeasurement) {
      case 'QUARTER':
        return t('pms.commons.QUARTER')
      case 'HALF_HOUR':
        return t('pms.commons.HALF_HOUR')
      case 'HOUR':
        return t('pms.commons.HOUR')
      case 'HALF_DAY':
        return t('pms.commons.HALF_DAY')
      case 'DAY':
      default:
        return t('pms.commons.DAY')
    }
  }, [props.unitOfMeasurement])

  return (
    <Stack direction="row" spacing={3} alignItems="flex-start">
      <FormControl>
        <FormItem fieldLabel={t('pms.booking.reservation_new_time.rate_unit', { udmLabel })} />
        <Typography variant="body" sx={{ mt: 0.75 }}>
          {formatCurrency(props.rackRate, currency)}
        </Typography>
      </FormControl>

      <FormControl>
        <FormItem fieldLabel={t('pms.booking.reservation_new_time.price_for_date_range', { from: formatDate(props.reservationTime.from, 'p'), to: formatDate(props.reservationTime.to, 'p') })} />
        <ResettableTextField //
          initialValue={calculatedDailyPrice}
          value={props.value ?? calculatedDailyPrice}
          onChange={props.onChange}
          changeOnBlur
          resetPosition={'RIGHT'}
        />
      </FormControl>

      <FormControl>
        <FormItem fieldLabel={t('pms.booking.reservation_new_time.total')} />
        <Typography variant="body" sx={{ mt: 0.75 }}>
          <b> {formatCurrency((props.value || calculatedDailyPrice) * props.days * props.quantity, currency)}</b>
        </Typography>
      </FormControl>
    </Stack>
  )
}
